<template>
    <div class="flex flex-col justify-center" style="width: 850px">
        <div class="text-center mb-24">
            <p class="font-semibold text-heading-1">
                {{ $t('campaigns.package_overview.title') }}
            </p>
            <p class="text-medium">
                {{ $t('campaigns.package_overview.description') }}
            </p>
        </div>
        <div
            class="grid grid-cols-2 gap-16 mx-auto"
            :class="{'mb-16': form.campaign_package_type_id}"
        >
            <select-dropdown
                :autoTranslate="false"
                icon="location-on-filled"
                icon-color="fill-primary"
                :options="getCountriesSelect"
                searchable
                :value="form.country"
                @change="form.country = $event"
            />
            <select-dropdown
                :autoTranslate="false"
                icon="language"
                icon-color="fill-primary"
                :options="getLocaleOptions"
                :value="form.locale"
                @change="form.locale = $event"
            />
            <template v-if="form.country && form.locale">
                <loading v-if="fetchingCampaignPackageTypes" class="col-span-full"/>
                <template v-else-if="!form.campaign_package_type_id">
                    <button
                        v-if="form.country && form.locale"
                        v-for="type in campaignPackageTypes"
                        class="w-240 text-body-sm p-24 rounded text-center text-primary border border-primary-80 hover:border-primary hover:bg-primary-5"
                        :class="`${type.id === form.campaign_package_type_id ? 'border-primary' : 'border-primary-20'}`"
                        @click="form.campaign_package_type_id = type.id"
                    >
                        <img :src="`/app/img/campaign-packages/${typeImage(type)}`" alt="collar"
                             class="inline-block w-128 h-128 mx-16">
                        <span class="block font-semibold uppercase mt-12 mb-4">
                            {{ translation(type.translations, locale, 'title', true, fallbackLocale) }}
                        </span>
                        <span class="text-primary-80">
                            {{ translation(type.translations, locale, 'description', true, fallbackLocale) }}
                        </span>
                    </button>
                </template>
                <template v-else>
                    <button
                        v-for="type in campaignPackageTypes"
                        class="w-240 inline-flex items-center gap-16 text-body-sm p-8 rounded text-center border"
                        :class="`${type.id === form.campaign_package_type_id ? 'border-primary bg-primary text-white' : 'border-primary-80 text-primary hover:border-primary hover:bg-primary-5'}`"
                        @click="form.campaign_package_type_id = type.id"
                    >
                        <img :src="`/app/img/campaign-packages/${typeImage(type)}`" alt="collar" class="w-32 h-32">
                        <span class="font-semibold">
                            {{ translation(type.translations, locale, 'title', true, fallbackLocale) }}
                        </span>
                    </button>
                </template>
            </template>

        </div>

        <template v-if="form.campaign_package_type_id">
            <Loading v-if="fetchingCampaignPackages" class="mb-16"/>
            <div v-else class="grid grid-cols-3 gap-16">
                <div v-for="campaignPackage in campaignPackages"
                     class="text-center border border-primary rounded py-20 px-24">
                    <div class="flex justify-center h-144">
                        <img :src="`/app/img/campaign-packages/${campaignPackage.img_src}`" alt="influx-image">
                    </div>
                    <div class="my-16">
                        <div class="text-center mb-8 font-semibold text-body">
                            {{ translation(campaignPackage.translations, locale, 'title', true, fallbackLocale) }}
                        </div>

                        <price :item="campaignPackage" css-class="mb-8"/>

                        <p>
                            {{ translation(campaignPackage.translations, locale, 'description', true, fallbackLocale) }}
                            *
                        </p>
                    </div>
                    <div class="flex justify-center my-24">
                        <campaign-package-icons :campaign-package-id="campaignPackage.id"/>
                    </div>
                    <div class="mt-32">
                        <loading v-if="requestingCampaign" size="small"/>
                        <simple-button
                            class="w-full"
                            look="primary"
                            v-else
                            @click="createCampaign(campaignPackage.id)"
                        >
                            {{ $t('jobs.sharing.request') }}
                        </simple-button>
                    </div>
                </div>
            </div>

            <p class="italic text-center text-primary-40 my-16">
                * {{ $t('campaigns.package_overview.disclaimer') }}
            </p>

            <div class="flex flex-col items-center gap-8">
                <simple-button
                    :loading="requestingCampaign"
                    look="secondary"
                    @click="createCampaign()"
                >
                    {{ $t('campaign_packages.create_package') }}
                </simple-button>
                <simple-button
                    look="text"
                    @click="$router.push({name: 'jobs.edit', params: {id: job.id}, query: {from: 'jobs', step: 5}})"
                >
                    {{ $t('campaign_packages.see_all_options') }}
                </simple-button>
            </div>
        </template>

    </div>
</template>

<script>
import SvgIcon from "./ui/SvgIcon.vue";
import CampaignPackageType from "../models/CampaignPackageType";
import {findTranslation, getCountriesSelectOptions} from "../../shared/helpers";
import {mapState} from "pinia";
import {useRepo} from 'pinia-orm';
import {useMainStore} from '../state/store';
import CampaignPackage from "../models/CampaignPackage";
import Form from "../etc/forms/Form";
import SelectDropdown from "./forms/SelectDropdown.vue";
import Loading from "./ui/Loading.vue";
import CampaignPackageIcons from "./CampaignPackageIcons.vue";
import SimpleButton from "./ui/SimpleButton.vue";
import Price from "./Price.vue";

export default {
    name: "CampaignPackageOverview",
    components: {Price, SimpleButton, CampaignPackageIcons, Loading, SelectDropdown, SvgIcon},
    props: {
        job: Object
    },
    data: function () {
        return {
            fetching: false,
            fetchingCampaignPackageTypes: false,
            fetchingCampaignPackages: false,
            requestingCampaign: false,

            form: new Form([
                {name: 'campaign_package_type_id', value: null},
                {name: 'campaign_package_id', value: null},
                {name: 'country', value: null},
                {name: 'locale', value: null},
                {name: 'job_id', value: this.job.id}
            ])
        }
    },
    computed: {
        ...mapState(useMainStore, ['selectedLocale']),
        getCountriesSelect() {
            return getCountriesSelectOptions(this.$i18n);
        },
        getLocaleOptions() {
            return Object.keys(this.job.active).filter(locale => this.job.active[locale]).map(locale => ({
                value: locale,
                label: this.$t('languages.' + locale + '.title')
            }));
        },
        campaignPackageTypes() {
            return useRepo(CampaignPackageType).get();
        },
        campaignPackages() {
            return useRepo(CampaignPackage)
                .orderBy('order', 'asc')
                .where('type_id', this.form.campaign_package_type_id)
                .get();
        },
        locale() {
            return this.selectedLocale;
        },
        fallbackLocale() {
            return Jobtoolz?.config?.translatable?.fallback_locale;
        },
    },
    methods: {
        translation(translations, locale, label, fallback, fallbackLocale) {
            return findTranslation(translations, locale, label, fallback, fallbackLocale);
        },
        createCampaign(packageId) {
            this.requestingCampaign = true;
            this.form.campaign_package_id = packageId;

            this.form.submit(CampaignPackage.createCampaign)
                .then(data => {
                    if (data.campaign_id) {
                        this.$router.push({name: 'campaign', params: {id: data.campaign_id}});
                    }

                    this.requestingCampaign = false;
                })
        },
        typeImage(type) {
            // TODO: move to db
            if (type.id === 2009270452) {
                return '__white-collar.svg'
            } else {
                return '__blue-collar.svg'
            }
        },
        edit(share = false) {
            this.$router.push({
                name: 'jobs.edit',
                params: {
                    id: this.job.id
                },
                query: {
                    from: 'jobs',
                    step: share ? 5 : 1
                }
            })
        },
        fetchCampaignPackages() {
            this.fetchingCampaignPackages = true;

            CampaignPackage.fetch(this.form.country)
                .then((res) => {
                    useRepo(CampaignPackage).save(res);
                    this.fetchingCampaignPackages = false;
                })
        }
    },
    watch: {
        'form.country': function (value) {
            this.form.campaign_package_type_id = null;

            if (value) {
                this.fetchCampaignPackages();
            } else {
                useRepo(CampaignPackage).flush();
            }
        }
    },
    created() {
        if (!useRepo(CampaignPackageType).first()) {
            this.fetchingCampaignPackageTypes = true;
            CampaignPackageType.fetch().then((res) => {
                useRepo(CampaignPackageType).save(res);
                this.fetchingCampaignPackageTypes = false}
            );
        }
        useRepo(CampaignPackage).flush();

        if (this.job.active_location?.country) {
            this.form.country = this.job.active_location.country;
        }

        const activeLanguages = this.getLocaleOptions;
        if (activeLanguages.length === 1) {
            this.form.locale = activeLanguages[0].value;
        }
    }
}
</script>
